import { observer } from 'mobx-react-lite'
import { Row, Col, Input, Select, Divider, InputNumber, Switch } from "antd";
import controller from './settings.controller'
import i from '../../../translations/i'
import { Titles } from '../../../layouts/Titles'
import React, { useEffect, useState } from 'react'
import settingsController from './settings.controller'
import projectController from "../project/project.controller";
import { storeLocal } from "../../../helpers/stores";
import { ErrorByPricingPlan } from "../../../components/ErrorByPricingPlan";
import OptionTypesController from "../../../components/options/optionTypes.controller";
import OptionValuesController from "../../../components/options/optionValues.controller";
import { WarehouseScheduleSection } from "./WarehouseScheduleSection";
import { WeekScheduleEditor, WeekScheduleRow, DEFAULT_WEEK_SCHEDULE, parseWeekScheduleValue } from "../../../components/WeekScheduleEditor";
import { SectionBadge, MasterToggle, ToggleField, FieldBlock } from "../../../components/SectionCard";
import { ClockCircleOutlined, ShopOutlined, QrcodeOutlined, DollarOutlined, SettingOutlined } from "@ant-design/icons";

const ORDER_ACCEPTANCE_OPTION_KEYS = [
    'orderAcceptanceSchedule', 'orderAcceptanceAllowPreorders',
    'orderAcceptanceClosedDescription', 'orderAcceptancePreorderDescription',
]

const cardStyle: React.CSSProperties = { width: '100%', display: 'flex', flexDirection: 'column', gap: 16, alignSelf: 'flex-start' }

// Общий Options/OptionBool монтируется до того, как приходят реальные данные,
// и хранит "сырое" DTO и редактируемое значение под одним и тем же ключом —
// после первой записи значение из бэкенда теряется и больше не подхватывается.
// Поэтому текстовые поля тоже читают/пишут OptionValuesController напрямую,
// гидратируясь один раз, когда данные реально готовы. Если ничего не сохранено —
// подставляем type.default (тот же дефолт, что отдаётся клиентскому приложению).
const OptionTextField = observer(({ optionKey, label }: { optionKey: string, label?: string }) => {

  const type = OptionTypesController.items.find((t: any) => t.key === optionKey)
  const valuesReady = OptionValuesController.render

  const [value, setValue] = useState('')
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    if(hydrated || !valuesReady || !type) return

    const dto = OptionValuesController.items[optionKey]
    const initial = (dto && typeof dto === 'object' ? dto.value_text : null) ?? type.default ?? ''

    setValue(initial)
    OptionValuesController.onChangeValue(optionKey, initial)
    setHydrated(true)
  }, [hydrated, valuesReady, type])

  if(!type) return null

  return (
    <FieldBlock label={label ?? type.label}>
      <Input.TextArea rows={3} value={value} onChange={(e) => {
        setValue(e.target.value)
        OptionValuesController.onChangeValue(optionKey, e.target.value)
      }} />
    </FieldBlock>
  )
})

// Показываем ровно то описание, которое реально относится к текущему состоянию
// тумблера "Принимать предзаказы" — второе клиенту в этот момент не покажется,
// незачем его тут же редактировать.
const AcceptanceClosedScreenFields = observer(() => {

  const dto = OptionValuesController.items['orderAcceptanceAllowPreorders']
  const preordersAllowed = typeof dto === 'boolean' ? dto : false

  // Оба поля должны быть смонтированы всегда, иначе то, что скрыто, никогда
  // не гидратируется из сырого DTO в строку — и при сохранении формы уходит
  // как есть, а FormData.append() сериализует объект в буквальное "[object Object]".
  // Поэтому прячем ненужное через display, а не через условный рендер.
  return (
    <>
      <div style={{ display: preordersAllowed ? 'block' : 'none' }}>
        <OptionTextField optionKey="orderAcceptancePreorderDescription" />
      </div>
      <div style={{ display: preordersAllowed ? 'none' : 'block' }}>
        <OptionTextField optionKey="orderAcceptanceClosedDescription" />
      </div>
    </>
  )
})

const Ordering = observer(() => {

    return(
        <>
          {!settingsController.project.ordering &&
            <FieldBlock label={i.t('settings:order.orderingPauseReason')}>
              <Input.TextArea
                required
                rows={3}
                maxLength={255}
                placeholder="Проводятся технические работы"
                value={settingsController.project.ordering_description}
                onChange={(e) => settingsController.onChangeProject('ordering_description', e.target.value)}
              />
            </FieldBlock>
          }
        </>
    )
})

const Cutlery = observer(() => {

    return(
        <>
          {settingsController.project.cutlery > 0 &&
            <FieldBlock label={i.t('settings:order.cutleryDescription')}>
              <Input.TextArea
                rows={3}
                maxLength={255}
                value={settingsController.project.cutlery_description}
                onChange={(e) => settingsController.onChangeProject('cutlery_description', e.target.value)}
              />
            </FieldBlock>
          }
        </>
    )
})

const ReceiveCode = observer(() => {

  return(
    <>
      {!!projectController.project.code_receive_order_enabled &&
        <>
          <FieldBlock label={i.t('settings:project.code_receive_order_type')}>
            <Select
              value={projectController.project.code_receive_order_type}
              style={{ width: '100%' }}
              onChange={e => projectController.project.code_receive_order_type = e}
              options={[
                {value: 'qrcode', label: "QR code"},
                {value: 'barcode', label: "Barcode"},
              ]}
            />
          </FieldBlock>
          <FieldBlock label={i.t('settings:project.code_receive_order_format')}>
            <p style={{ marginBottom: 8 }}>
              N - {i.t('def:Random number')}<br/>
              L - {i.t('def:Random letter')}
            </p>
            <Input
              value={projectController.project.code_receive_order_format}
              onChange={e => projectController.handleChangeReceiveCode(e.target.value)}
            />
          </FieldBlock>
        </>
      }
    </>
  )
})

const ServiceFeeFields = observer(() => {

  return(
    <>
      {!!projectController.project.service_fee &&
        <>
          <FieldBlock label={i.t('settings:order.serviceFeeName')}>
            <Input
              onChange={(e) => projectController.project.service_fee_name = e.target.value}
              value={projectController.project.service_fee_name}
            />
          </FieldBlock>
          <FieldBlock label={i.t('settings:order.serviceFeeDescription')}>
            <Input.TextArea
              onChange={(e) => projectController.project.service_fee_description = e.target.value}
              value={projectController.project.service_fee_description}
            />
          </FieldBlock>
          <FieldBlock label={i.t('settings:order.serviceFeeUnit')}>
            <Select
              options={[
                {label: i.t('settings:order.serviceFeeUnitFixed'), value: 0},
                {label: i.t('settings:order.serviceFeeUnitPercent'), value: 1}
              ]}
              value={projectController.project.service_fee_unit}
              onChange={(e) => projectController.project.service_fee_unit = e}
              style={{ width: '100%' }}
            />
          </FieldBlock>
          <Row gutter={[16, 16]}>
            <Col span={projectController.project.service_fee_unit === 1 ? 8 : 24}>
              <FieldBlock label={i.t('settings:order.serviceFeeAmount')}>
                <InputNumber
                  onChange={(e) => projectController.project.service_fee_amount = e ?? 0}
                  value={projectController.project.service_fee_amount}
                  style={{ width: '100%' }}
                />
              </FieldBlock>
            </Col>
            {projectController.project.service_fee_unit === 1 &&
              <>
                <Col span={8}>
                  <FieldBlock label={i.t('settings:order.serviceFeeMin')}>
                    <InputNumber
                      onChange={(e) => projectController.project.service_fee_min = e}
                      value={projectController.project.service_fee_min}
                      style={{ width: '100%' }}
                    />
                  </FieldBlock>
                </Col>
                <Col span={8}>
                  <FieldBlock label={i.t('settings:order.serviceFeeMax')}>
                    <InputNumber
                      onChange={(e) => projectController.project.service_fee_max = e}
                      value={projectController.project.service_fee_max}
                      style={{ width: '100%' }}
                    />
                  </FieldBlock>
                </Col>
              </>
            }
          </Row>
        </>
      }
    </>
  )
})

// Общий Options/OptionBool монтируется до того, как приходят реальные данные,
// и хранит "сырое" DTO и редактируемое значение под одним и тем же ключом —
// после первой записи значение из бэкенда теряется и больше не подхватывается.
// Поэтому эти два поля читают/пишут OptionValuesController напрямую, гидратируясь
// один раз, когда данные реально готовы (OptionValuesController.render).
const AcceptanceScheduleField = observer(() => {

  const type = OptionTypesController.items.find((t: any) => t.key === 'orderAcceptanceSchedule')
  const valuesReady = OptionValuesController.render

  const [rows, setRows] = useState<WeekScheduleRow[]>(DEFAULT_WEEK_SCHEDULE)
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    if(hydrated || !valuesReady) return

    const dto = OptionValuesController.items['orderAcceptanceSchedule']
    const initial = parseWeekScheduleValue(dto && typeof dto === 'object' ? dto.value_text : null)

    setRows(initial)
    OptionValuesController.onChangeValue('orderAcceptanceSchedule', JSON.stringify(initial))
    setHydrated(true)
  }, [hydrated, valuesReady])

  if(!type) return null

  return (
    <FieldBlock label={type.label}>
      <WeekScheduleEditor rows={rows} onChange={(next) => {
        setRows(next)
        OptionValuesController.onChangeValue('orderAcceptanceSchedule', JSON.stringify(next))
      }} />
    </FieldBlock>
  )
})

const AcceptancePreordersToggle = observer(() => {

  const type = OptionTypesController.items.find((t: any) => t.key === 'orderAcceptanceAllowPreorders')
  const valuesReady = OptionValuesController.render

  const [checked, setChecked] = useState(false)
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    if(hydrated || !valuesReady) return

    const dto = OptionValuesController.items['orderAcceptanceAllowPreorders']
    const initial = dto && typeof dto === 'object' ? !!dto.value_int : !!type.default

    setChecked(initial)
    OptionValuesController.onChangeValue('orderAcceptanceAllowPreorders', initial)
    setHydrated(true)
  }, [hydrated, valuesReady])

  if(!type) return null

  return (
    <ToggleField
      label={type.label}
      checked={checked}
      onChange={(e) => {
        setChecked(e)
        OptionValuesController.onChangeValue('orderAcceptanceAllowPreorders', e)
      }}
    />
  )
})

export const ShopSettingsOrder = observer(() => {

    useEffect(() => {
      projectController.get()
      settingsController.getProject()
      OptionTypesController.get([], ORDER_ACCEPTANCE_OPTION_KEYS)
        .then((keys: string[]) => OptionValuesController.get(keys))
    }, [])

    const buttons = [
        { title: i.t('def:actions.back'), href: '/shop/settings', type: 'dashed' },
        { title: i.t('def:actions.save'), htmlType: `submit`, type: 'primary' }
    ]

    const receiveCodeDisabled = !!storeLocal.get('black_mark') || storeLocal.get('pricing_plan') < 2
    const serviceFeeDisabled = !!storeLocal.get('black_mark') || storeLocal.get('pricing_plan') < 2

    return(
        <>
            <form onSubmit={(e) => {
              settingsController.save(e);
              projectController.saving()
              OptionValuesController.save(e)
            }}>
                <Titles
                    title={i.t('settings:order.title')}
                    buttons={buttons}
                />

                <Row gutter={[20, 20]}>
                  <Col span={24} xl={12}>
                    <div style={cardStyle}>
                      <SectionBadge
                        icon={<ClockCircleOutlined />}
                        text={i.t('settings:order.scheduleTitle')}
                        background="linear-gradient(135deg, var(--primary-color), #9186e0)"
                      />
                      <MasterToggle>
                        <ToggleField
                          label={i.t('settings:settings.ordering.title')}
                          checked={!!settingsController.project.ordering}
                          onChange={(e) => settingsController.project.ordering = +e}
                        />
                      </MasterToggle>
                      <Ordering />
                      <AcceptanceScheduleField />
                      <MasterToggle>
                        <AcceptancePreordersToggle />
                      </MasterToggle>
                      <AcceptanceClosedScreenFields />
                    </div>
                  </Col>
                  <Col span={24} xl={12}>
                    <div style={cardStyle}>
                      <SectionBadge
                        icon={<ShopOutlined />}
                        text={i.t('settings:order.scheduleByWarehouse')}
                        background="linear-gradient(135deg, #3f3d56, #63607f)"
                      />
                      <WarehouseScheduleSection />
                    </div>
                  </Col>

                  <Col span={24} xl={12}>
                    <div style={cardStyle}>
                      <SectionBadge
                        icon={<SettingOutlined />}
                        text={i.t('settings:order.parametersTitle')}
                        background="linear-gradient(135deg, #3f3d56, #63607f)"
                      />
                      <ToggleField
                        label={i.t('settings:settings.cutlery.cutlery')}
                        checked={!!settingsController.project.cutlery}
                        onChange={(e) => settingsController.project.cutlery = +e}
                      />
                      <Cutlery />
                      <ToggleField
                        label={i.t('settings:label.attributes')}
                        checked={!!controller.project.deliveryattributes}
                        onChange={(e) => controller.project.deliveryattributes = +e}
                      />
                      <FieldBlock label={i.t('settings:label.comment')}>
                        <p style={{ marginBottom: 8 }}>{i.t('settings:settings.placeholder')}</p>
                        <Input.TextArea
                          rows={3}
                          maxLength={255}
                          onChange={(e) => controller.onChangeProject('ordercomment', e.target.value)}
                          value={controller.project.ordercomment}
                        />
                      </FieldBlock>
                      <FieldBlock label={i.t('settings:order.maxOrderWeight')}>
                        <p style={{ marginBottom: 8 }}>{i.t('settings:order.maxOrderWeightHint')}</p>
                        <InputNumber
                          style={{ width: '100%' }}
                          onChange={(e) => projectController.project.order_max_weight = e}
                          value={projectController.project.order_max_weight}
                        />
                      </FieldBlock>
                    </div>
                  </Col>

                  <Col span={24} xl={12}>
                    <div style={cardStyle}>
                      <SectionBadge
                        icon={<QrcodeOutlined />}
                        text={i.t('settings:settings.order_receive_code')}
                        background="linear-gradient(135deg, var(--primary-color), #9186e0)"
                        toggle={
                          <ErrorByPricingPlan plan={2} content={
                            <Switch
                              checked={!!projectController.project.code_receive_order_enabled}
                              onChange={(e) => projectController.project.code_receive_order_enabled = e}
                              checkedChildren={i.t('def:on')}
                              unCheckedChildren={i.t('def:off')}
                              disabled={receiveCodeDisabled}
                            />
                          } />
                        }
                      />
                      <ReceiveCode />
                    </div>
                  </Col>

                  <Col span={24}>
                    <div style={cardStyle}>
                      <SectionBadge
                        icon={<DollarOutlined />}
                        text={i.t('settings:order.serviceFee')}
                        background="linear-gradient(135deg, #3f3d56, #63607f)"
                        toggle={
                          <ErrorByPricingPlan plan={2} content={
                            <Switch
                              checked={!!projectController.project.service_fee}
                              onChange={(e) => projectController.project.service_fee = e}
                              checkedChildren={i.t('def:on')}
                              unCheckedChildren={i.t('def:off')}
                              disabled={serviceFeeDisabled}
                            />
                          } />
                        }
                      />
                      <ServiceFeeFields />
                    </div>
                  </Col>
                </Row>
            </form>
        </>
    )
})
